package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.StoryViewerDto
import com.arena_web.domain.repositories.UserStoryRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*

// Figma 8682:456599 — the "Who saw this story?" surface + the red like badge.
private const val LIKE_BADGE_BG = "#ff4242"
private const val LIKE_BADGE_RING = "#091a40"

/**
 * "Who saw this story?" bottom sheet (Figma 8682:456190) — a SECOND sheet that
 * pops over the story-viewer sheet when its "Seen by" pill is tapped. Lists
 * everyone who viewed [storyId] (most-recent first) as a mini player card +
 * name, with a red heart badge on viewers who also liked the story.
 *
 * Rendered as a sibling of [MobileStoryViewerSheet] (not nested inside it) so
 * its fixed scrim/panel aren't trapped by the first sheet's transformed panel.
 */
@Composable
fun MobileStoryViewersSheet(
    storyId: String,
    storyRepository: UserStoryRepository?,
    sportName: String,
    onDismiss: () -> Unit,
) {
    val t = LocalAppStrings.current
    var viewers by remember(storyId) { mutableStateOf<List<StoryViewerDto>>(emptyList()) }
    var loading by remember(storyId) { mutableStateOf(true) }

    LaunchedEffect(storyId, storyRepository) {
        val repo = storyRepository ?: run { loading = false; return@LaunchedEffect }
        loading = true
        viewers = repo.getStoryViewers(storyId).getOrNull().orEmpty()
        loading = false
    }

    BottomSheet(onDismiss = onDismiss, contentGap = 18) {
        // Count row — people glyph + total viewers (Figma 8682:456601).
        Row(
            modifier = Modifier.sGap(10),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = AppIcons.People, modifier = Modifier.sSize(24))
            SpanText(
                "${viewers.size}",
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary),
            )
        }

        // Header + viewer list (Figma 8682:456604).
        Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
            SpanText(
                t.profileStoryViewersTitle,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.Text.Secondary),
            )
            when {
                loading && viewers.isEmpty() -> ViewersHint(t.commonLoading)
                viewers.isEmpty() -> ViewersHint(t.profileStoryViewersNoViews)
                else -> Column(
                    modifier = HiddenScrollbarStyle.toModifier()
                        .fillMaxWidth()
                        .styleModifier {
                            // Cap the list so a long viewer list scrolls inside
                            // the sheet rather than pushing it past 92vh.
                            property("max-height", "55vh")
                            property("overflow-y", "auto")
                        }
                        .sGap(10),
                ) {
                    viewers.forEach { viewer ->
                        ViewerRow(viewer = viewer, sportName = sportName)
                    }
                }
            }
        }
    }
}

@Composable
private fun ViewersHint(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(14)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(22)
            .color(AppColors.Text.Muted),
    )
}

@Composable
private fun ViewerRow(viewer: StoryViewerDto, sportName: String) {
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Mini player card (Figma 32×48 — we use the project's Highlight 37×58
        // preset, matching the comments/identity mini cards) + like badge.
        Box(modifier = Modifier.styleModifier { property("position", "relative"); property("flex-shrink", "0") }) {
            if (viewer.cardImageUrl != null) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = viewer.cardImageUrl,
                    cardAssetsBasePath = viewer.cardAssetsBasePath,
                    cardTextColor = viewer.cardTextColor,
                    avatarUrl = viewer.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            } else {
                // No card yet → bust fallback, same as the viewer IdentityRow.
                Box(
                    modifier = Modifier
                        .sWidth(PlayerCardSize.Highlight.w)
                        .sHeight(PlayerCardSize.Highlight.h)
                        .sBorderRadius(8)
                        .styleModifier {
                            property("background-color", "#1A2238")
                            val a = viewer.avatarUrl
                            if (a != null) {
                                property("background-image", "url('$a')")
                                property("background-size", "cover")
                                property("background-position", "center")
                            }
                        },
                )
            }
            if (viewer.likedByThem) {
                // Red heart badge bottom-right — viewer also liked the story.
                Box(
                    modifier = Modifier
                        .sSize(14)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("position", "absolute")
                            property("right", "0")
                            property("bottom", "0")
                            property("background", LIKE_BADGE_BG)
                            property("border", "0.8px solid $LIKE_BADGE_RING")
                            property("box-sizing", "border-box")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        src = AppIcons.Heart,
                        modifier = Modifier.sSize(7).styleModifier {
                            // White heart over the red disc.
                            property("filter", "brightness(0) invert(1)")
                        },
                    )
                }
            }
        }
        // Display name (full name), falling back to the handle if blank.
        SpanText(
            viewer.fullName.ifBlank { viewer.username },
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(AppColors.Text.Secondary),
        )
    }
}
